import {
  type Dispatch,
  type PropsWithChildren,
  type SetStateAction,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useBrowserStore } from '@/hooks/data/browser/useBrowserStore';
import { type ChildNodeType } from '@/types/tree';
import { isSelectableAsset } from '@/lib/utils';
import { type Asset } from '@/types/asset';

export type SelectedNode = {
  id: string;
  name: string;
  slug: string;
  type: ChildNodeType;
};

/**
 * The gallery selection is *either* assets or one child node, never both. A mixed selection is
 * unrepresentable rather than merely discouraged: neither arm has a slot to put the other's
 * items in, so the compiler rejects it at construction. Proved by the type-level test in
 * `__tests__/types/gallery-selection.test.ts`.
 *
 * The nodes arm holds a single `item`, not an array: only one child can be selected at a time, and
 * an array left that rule to a comment plus four readers each doing `items[0]` or `.length > 0`.
 * It also gave "nothing selected" two spellings — `{kind:'nodes', items: []}` as well as
 * `{kind:'assets', items: []}` — and the first one silently disabled
 * `useReconcileSelectionWithPage`, which keys off `kind` alone. Deselecting a child now returns to
 * the empty assets arm, so there is exactly one empty state.
 *
 * Read `selection` when you care which kind it is. Read `selectedAssets` (the projection
 * below) when you only ever act on assets.
 */
export type GallerySelection = { kind: 'assets'; items: Array<Asset> } | { kind: 'nodes'; item: SelectedNode };

export type GallerySelectionTypes = {
  asset: Asset;
  assets: Array<Asset>;
  index: number;
  metaKey?: boolean;
  shiftKey?: boolean;
  ctrlKey?: boolean;
};

type GallerySelectionContextType = {
  selection: GallerySelection;
  /**
   * Asset-arm projection of `selection`: the selected assets, or empty when a child node is
   * selected instead. Deliberately never widens to include nodes — an asset-only consumer
   * seeing "nothing selected" while a folder is selected is the correct answer for it.
   * Consumers that must act on a selected child read `selection` (or `useSelection`'s
   * `selectedChild`) instead.
   */
  selectedAssets: Array<Asset>;
  toggleItemSelection: ({ asset, assets, index, metaKey, shiftKey, ctrlKey }: GallerySelectionTypes) => Array<Asset>;
  toggleNodeItemSelection: (node: SelectedNode) => void;
  /**
   * Replace the selected child's snapshot, or clear it with `undefined`. `SelectedNode` is a copy
   * taken at click time, so a rename or a trash of the selected child would otherwise leave the
   * selection bar, the inspector header and the DownloadZip filename describing a stale node.
   * Driven by `useReconcileChildSelection`.
   */
  setSelectedNode: (node: SelectedNode | undefined) => void;
  deselectAllItems: () => void;
  contextItemSelection: (asset: Asset) => void;
  /**
   * The node counterpart of `contextItemSelection`: right-clicking a child folder/album selects it,
   * the way right-clicking an asset selects the asset.
   *
   * Distinct from `toggleNodeItemSelection` because a context menu must never deselect — opening the
   * menu on the already-selected child would otherwise clear the selection its own items act on.
   */
  contextNodeItemSelection: (node: SelectedNode) => void;
  setSelectedAssets: Dispatch<SetStateAction<Array<Asset>>>;
  /**
   * The asset a click/toggle most recently acted on, independent of `selectedAssets`' order and
   * of "select all" (which represents everything via `isAllSelected` rather than by always
   * keeping the clicked asset's id in `selectedAssets` — see `selection-bar.tsx`). Enter-to-open
   * reads this rather than array order or a focused row, because neither reliably names "the last
   * one selected": a shift-click range can leave its clicked endpoint anywhere in the (deduped,
   * order-preserving) array, and select-all doesn't necessarily keep it there at all.
   *
   * Derived, not just written and trusted: it reads back as undefined once the tracked asset is
   * no longer actually part of the selection (checked against `selectedAssets`, or waived while
   * `isAllSelected`). `setSelectedAssets` is a public escape hatch several callers use directly —
   * "Clear Selection" (selection-bar.tsx) writes `[]` straight through it rather than via
   * `deselectAllItems` — so trusting the last write from `toggleItemSelection`/`contextItemSelection`
   * alone would leave this pointing at an asset that isn't selected anymore.
   */
  lastSelectedAsset: Asset | undefined;
};

const GallerySelectionContext = createContext<GallerySelectionContextType | undefined>(undefined);

const EMPTY_ASSETS: Array<Asset> = [];

export function GallerySelectionProvider({ children }: PropsWithChildren) {
  const [selection, setSelection] = useState<GallerySelection>({ kind: 'assets', items: [] });
  const [lastSelectedIndex, setLastSelectedIndex] = useState(-1);
  const [lastSelectedAssetRaw, setLastSelectedAsset] = useState<Asset | undefined>(undefined);
  const isAllSelected = useBrowserStore((s) => s.isAllSelected);

  const selectedAssets = selection.kind === 'assets' ? selection.items : EMPTY_ASSETS;

  // See the exported type's doc comment: self-corrects against writes that bypass the
  // toggle/context actions above (setSelectedAssets is public), instead of trusting whichever of
  // them wrote last.
  const lastSelectedAsset = useMemo(() => {
    if (!lastSelectedAssetRaw) {
      return undefined;
    }
    if (isAllSelected || selectedAssets.some((a) => a.id === lastSelectedAssetRaw.id)) {
      return lastSelectedAssetRaw;
    }
    return undefined;
  }, [lastSelectedAssetRaw, isAllSelected, selectedAssets]);

  // Writing assets always lands on the assets arm, so this can never produce a mix. Writing
  // an empty array is also how a selected child gets cleared.
  const setSelectedAssets: Dispatch<SetStateAction<Array<Asset>>> = useCallback((updater) => {
    setSelection((prev) => {
      const prevAssets = prev.kind === 'assets' ? prev.items : EMPTY_ASSETS;
      // No cast needed: `typeof updater === 'function'` narrows SetStateAction<Array<Asset>> on its
      // own, because Array<Asset> is not callable and so cannot be the function arm.
      const next = typeof updater === 'function' ? updater(prevAssets) : updater;
      // An updater that hands back the array it was given is saying "nothing to change", so the
      // arm must not move: the live asset broadcasts write through here on every event, and
      // landing on the assets arm regardless would clear a selected child whenever any unrelated
      // asset elsewhere in the folder was trashed or deleted. Clearing still works, because
      // `setSelectedAssets([])` passes a fresh array rather than this one.
      if (next === prevAssets) {
        return prev;
      }
      return { kind: 'assets', items: next };
    });
  }, []);

  const contextItemSelection = useCallback(
    (asset: Asset) => {
      // Same gate the click pool uses. The row already blocks its own handlers, but this is
      // the one selection path a caller can wire up directly (the public share gallery does),
      // so the rule belongs here too rather than only at the call sites.
      if (!isSelectableAsset(asset)) {
        return;
      }
      if (!(selection.kind === 'assets' && selection.items.some((a) => a.id === asset.id))) {
        setSelection({ kind: 'assets', items: [asset] });
      }
      setLastSelectedAsset(asset);
    },
    [selection],
  );

  // Single click selects exactly one child node, clicking the selected one again deselects.
  // "Exactly one" is now the shape of the nodes arm rather than a rule this writer enforces.
  const toggleNodeItemSelection = useCallback((node: SelectedNode) => {
    // Select-all is an asset concept and cannot coexist with a selected child. Left set, the
    // details panel counts every asset in the folder ("74 folders selected") and renders the
    // bulk-asset editor while the selection bar describes the one child — the two disagree.
    // Cleared here rather than at the click sites, because it is an invariant of the selection,
    // not of any one caller.
    useBrowserStore.setState({ isAllSelected: false });
    setSelection((prev) =>
      // Toggling off lands on the empty assets arm, not an empty nodes arm: "nothing selected" has
      // one representation, so consumers keyed on `kind` cannot mistake it for a live child.
      prev.kind === 'nodes' && prev.item.id === node.id ? { kind: 'assets', items: [] } : { kind: 'nodes', item: node },
    );
    setLastSelectedIndex(-1);
    setLastSelectedAsset(undefined);
  }, []);

  // Select, never toggle. Shares toggleNodeItemSelection's select-all invariant because it lands on
  // the same nodes arm, and clears the shift-click anchor for the same reason.
  const contextNodeItemSelection = useCallback((node: SelectedNode) => {
    useBrowserStore.setState({ isAllSelected: false });
    setSelection({ kind: 'nodes', item: node });
    setLastSelectedIndex(-1);
    setLastSelectedAsset(undefined);
  }, []);

  const setSelectedNode = useCallback((node: SelectedNode | undefined) => {
    setSelection((prev) => {
      if (node === undefined) {
        // Only clear a node selection; an asset selection is none of this function's business.
        return prev.kind === 'nodes' ? { kind: 'assets', items: [] } : prev;
      }
      return { kind: 'nodes', item: node };
    });
  }, []);

  const toggleItemSelection = useCallback(
    ({ asset, assets, index, metaKey, shiftKey, ctrlKey }: GallerySelectionTypes) => {
      let newSelectedAssetIds: Array<Asset>;

      // Plain click, select only this one
      if (!metaKey && !shiftKey && !ctrlKey) {
        // if this is the only thing selected, clear everything:
        if (selectedAssets.length === 1 && selectedAssets[0].id === asset.id) {
          newSelectedAssetIds = [];
        } else {
          // otherwise, select only this one
          newSelectedAssetIds = [asset];
        }

        // Shift+click, select range from last to this. Only valid when the anchor index still points to a
        // selected asset on the current page — guards against a stale anchor after paginating to a new page.
      } else if (
        shiftKey &&
        lastSelectedIndex >= 0 &&
        selectedAssets.some((a) => a.id === assets[lastSelectedIndex]?.id)
      ) {
        const [start, end] = lastSelectedIndex < index ? [lastSelectedIndex, index] : [index, lastSelectedIndex];
        const range = assets.slice(start, end + 1);
        // merge with existing (so you can extend a selection)
        newSelectedAssetIds = Array.from(new Map([...selectedAssets, ...range].map((a) => [a.id, a])).values());

        // Ctrl/Cmd+click, toggle this one in the existing set
      } else if (metaKey || ctrlKey) {
        const exists = selectedAssets.some((a) => a.id === asset.id);
        newSelectedAssetIds = exists ? selectedAssets.filter((a) => a.id !== asset.id) : [...selectedAssets, asset];
      } else {
        newSelectedAssetIds = [asset];
      }

      setSelectedAssets(newSelectedAssetIds);

      const wasDeselected = !newSelectedAssetIds.some((a) => a.id === asset.id);
      if (wasDeselected) {
        if (newSelectedAssetIds.length === 0) {
          setLastSelectedIndex(-1);
          setLastSelectedAsset(undefined);
        } else {
          const highestIndex = newSelectedAssetIds.reduce((max, a) => {
            const idx = assets.findIndex((x) => x.id === a.id);
            return idx > max ? idx : max;
          }, -1);
          setLastSelectedIndex(highestIndex);
          setLastSelectedAsset(assets[highestIndex]);
        }
      } else {
        setLastSelectedIndex(index);
        setLastSelectedAsset(asset);
      }

      return newSelectedAssetIds;
    },
    [lastSelectedIndex, selectedAssets, setSelectedAssets],
  );

  const deselectAllItems = useCallback(() => {
    setSelectedAssets([]);
    setLastSelectedIndex(-1);
    setLastSelectedAsset(undefined);
  }, [setSelectedAssets]);

  // Memoized because this provider sits near the root: without it, any re-render of the tree above
  // hands every consumer a new object, and the browser view now adds a consumer per child row.
  const value = useMemo(
    () => ({
      selection,
      selectedAssets,
      toggleItemSelection,
      toggleNodeItemSelection,
      setSelectedNode,
      deselectAllItems,
      contextItemSelection,
      contextNodeItemSelection,
      setSelectedAssets,
      lastSelectedAsset,
    }),
    [
      selection,
      selectedAssets,
      toggleItemSelection,
      toggleNodeItemSelection,
      setSelectedNode,
      deselectAllItems,
      contextItemSelection,
      contextNodeItemSelection,
      setSelectedAssets,
      lastSelectedAsset,
    ],
  );

  return <GallerySelectionContext.Provider value={value}>{children}</GallerySelectionContext.Provider>;
}

export function useGallerySelection() {
  const context = useContext(GallerySelectionContext);
  if (context === undefined) {
    throw new Error('useGallerySelection must be used within an GallerySelectionProvider');
  }
  return context;
}
